import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';
import Skeleton from './common/Skeleton';

const RISK_CONFIG = {
  PERMISSIVE:      { badge: 'success', label: 'Permissive',       icon: '🟢', desc: 'No restrictions on use or distribution.' },
  WEAK_COPYLEFT:   { badge: 'warning', label: 'Weak Copyleft',    icon: '🟡', desc: 'Modifications to this library must be shared under the same license.' },
  STRONG_COPYLEFT: { badge: 'danger',  label: 'Strong Copyleft',  icon: '🔴', desc: 'Derivative works may be subject to viral licensing (GPL/AGPL).' },
  UNKNOWN:         { badge: 'secondary',label: 'Unknown',         icon: '⚪', desc: 'License not identified or declared as NOASSERTION.' },
};

const RiskBadge = ({ risk }) => {
  const { t } = useTranslation();
  const cfg = RISK_CONFIG[risk] || RISK_CONFIG.UNKNOWN;
  const labelKey = { PERMISSIVE: 'permissive', WEAK_COPYLEFT: 'weakCopyleft', STRONG_COPYLEFT: 'strongCopyleft', UNKNOWN: 'unknown' }[risk] || 'unknown';
  const sevCls = { PERMISSIVE: 'low', WEAK_COPYLEFT: 'medium', STRONG_COPYLEFT: 'critical', UNKNOWN: 'none' }[risk] || 'none';
  const descKey = { PERMISSIVE: 'permissiveDesc', WEAK_COPYLEFT: 'weakCopyleftDesc', STRONG_COPYLEFT: 'strongCopyleftDesc', UNKNOWN: 'unknownDesc' }[risk] || 'unknownDesc';
  return (
    <span className={`ce-sev ce-sev--${sevCls}`} title={t(`licenses.${descKey}`, cfg.desc)}>
      {cfg.icon} {t(`licenses.${labelKey}`, cfg.label)}
    </span>
  );
};

const LicenseView = ({ repoName, sbomId, initialData }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState('');
  const [expandedLicense, setExpandedLicense] = useState(null);

  const riskFilter = searchParams.get('lic_filter') || 'ALL';
  const page = Math.max(1, parseInt(searchParams.get('lic_page') || '1', 10));
  const pageSize = parseInt(localStorage.getItem('depguard_page_size') || String(DEFAULT_PAGE_SIZE), 10);

  const setRiskFilter = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('lic_filter', val); n.set('lic_page', '1'); return n; });
  const setPage       = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('lic_page', String(val)); return n; });

  useEffect(() => {
    if (initialData !== undefined) return;
    if (!repoName || !sbomId) return;
    setLoading(true);
    setError('');
    axios
      .get(`${config.BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/licenses`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || err.message || t('licenses.loadError')))
      .finally(() => setLoading(false));
  }, [repoName, sbomId, t]);

  useEffect(() => {
    if (initialData === undefined) return;
    setData(initialData || null);
    setLoading(false);
  }, [initialData]);

  if (loading) return (
    <div style={{ padding: '1rem 0' }}>
      <Skeleton.Table rows={5} cols={4} />
      <div className="small text-secondary mt-3">{t('licenses.loading', 'Analyzing licenses...')}</div>
    </div>
  );

  if (error) return <div className="alert alert-danger">{t('licenses.loadError')}: {error}</div>;
  if (!data)  return null;

  const sorted = [...(data.licenses || [])].sort((a, b) => {
    // Sort: STRONG_COPYLEFT first, then WEAK, then PERMISSIVE, then UNKNOWN
    const order = { STRONG_COPYLEFT: 0, WEAK_COPYLEFT: 1, PERMISSIVE: 2, UNKNOWN: 3 };
    return (order[a.risk] ?? 4) - (order[b.risk] ?? 4);
  });

  const filtered = riskFilter === 'ALL' ? sorted : sorted.filter(e => e.risk === riskFilter);
  const nPages = Math.ceil(filtered.length / pageSize);
  const pagedFiltered = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      {/* Summary Cards */}
      <div className="ce-kpi-row">
        {[
          { label: t('licenses.totalPkgs', 'Total Pkgs'), value: data.totalPackages, color: '#6366f1' },
          { label: t('licenses.permissive', 'Permissive'), value: data.permissiveCount, color: '#16a34a' },
          { label: t('licenses.weakCopyleft', 'Weak Copyleft'), value: data.weakCopyleftCount, color: '#ca8a04' },
          { label: t('licenses.strongCopyleft', 'Strong Copyleft'), value: data.strongCopyleftCount, color: '#dc2626' },
          { label: t('licenses.unknown', 'Unknown'), value: data.unknownCount, color: '#6b7280' },
        ].map(card => (
          <div key={card.label} className="ce-kpi" style={{ '--kpi-color': card.color }}>
            <div className="ce-kpi-value">{card.value}</div>
            <div className="ce-kpi-label">{card.label}</div>
          </div>
        ))}
      </div>

      {data.strongCopyleftCount > 0 && (
        <div className="alert alert-danger small mb-3">
          ⚠️ <strong>{data.strongCopyleftCount}</strong> {t('licenses.strongCopyleftWarning', 'package(s) use strong copyleft licenses (GPL/AGPL). Review whether they affect the licensing of your own code.')}
        </div>
      )}

      {/* Risk filter */}
      <div className="ce-filter-bar">
        {['ALL', 'STRONG_COPYLEFT', 'WEAK_COPYLEFT', 'PERMISSIVE', 'UNKNOWN'].map(r => (
          <button
            key={r}
            className={`ce-btn ce-btn--sm ${riskFilter === r ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
            onClick={() => setRiskFilter(r)}
          >
            {r.replace('_', ' ')}
          </button>
        ))}
        <span className="ce-filter-count">{filtered.length} licenses</span>
      </div>

      {/* License table */}
      <div className="ce-table-wrap">
        <table className="ce-table">
          <thead>
            <tr>
              <th>{t('licenses.columns.license')}</th>
              <th>{t('licenses.columns.risk')}</th>
              <th>{t('licenses.columns.packages')}</th>
              <th>{t('licenses.columns.details')}</th>
            </tr>
          </thead>
          <tbody>
            {pagedFiltered.map(entry => (
              <React.Fragment key={entry.license}>
                <tr>
                  <td><code>{entry.license}</code></td>
                  <td><RiskBadge risk={entry.risk} /></td>
                  <td>{entry.count}</td>
                  <td>
                    <button
                      className="ce-btn ce-btn--secondary ce-btn--sm"
                      onClick={() => setExpandedLicense(expandedLicense === entry.license ? null : entry.license)}
                    >
                      {expandedLicense === entry.license ? t('licenses.hidePackages', 'Hide') : t('licenses.showPackages', 'Show packages')}
                    </button>
                  </td>
                </tr>
                {expandedLicense === entry.license && (
                  <tr>
                    <td colSpan={4} style={{ background: 'var(--bs-tertiary-bg)' }}>
                      <div className="ce-expand-panel" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {entry.packages.map(pkg => (
                          <span key={`${pkg.name}-${pkg.version}`} className="ce-tag">
                            {pkg.name}@{pkg.version}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {nPages > 1 && (
        <Pagination
          nPages={nPages}
          currentPage={page}
          setCurrentPage={setPage}
          pageSize={pageSize}
          totalItems={filtered.length}
        />
      )}
    </div>
  );
};

export default LicenseView;
